<template>
  <LoadingDiv v-if="!isReady" />

  <form v-else class="hmo-settings-form" @submit.prevent="handleSubmit">
    <section class="hmo-form-section">
      <div class="hmo-section-head">
        <span>Profile</span>
        <h5>General Information</h5>
        <p>Maintain the primary HMO identity and official contact details used across the application.</p>
      </div>

      <div class="hmo-field-grid">
        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="hmoName" type="text" v-model="hmoName" required />
            <label class="active" for="hmoName">HMO Name</label>
          </div>
        </div>

        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="hmoSlogan" type="text" v-model="hmoSlogan" required />
            <label class="active" for="hmoSlogan">HMO Slogan</label>
          </div>
        </div>

        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="email" type="email" v-model="email" required />
            <label class="active" for="email">Main Email</label>
          </div>
        </div>

        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="phone" type="text" v-model="phone" pattern="[0-9]{10,13}" required />
            <label class="active" for="phone">Main Phone</label>
          </div>
        </div>

        <div class="hmo-field-card hmo-field-wide">
          <div class="setup-field">
            <input id="addressHq" type="text" v-model="addressHq" required />
            <label class="active" for="addressHq">Head Office Address</label>
          </div>
        </div>

        <div class="hmo-field-card hmo-field-wide">
          <div class="setup-field">
            <textarea id="aboutHmo" v-model="aboutHmo" required></textarea>
            <label class="active" for="aboutHmo">About HMO</label>
          </div>
        </div>

        <div class="hmo-field-card hmo-field-wide">
          <div class="setup-field">
            <textarea id="generalNote" v-model="generalNote" required></textarea>
            <label class="active" for="generalNote">General Note</label>
          </div>
        </div>
      </div>
    </section>

    <section class="hmo-form-section">
      <div class="hmo-section-head hmo-section-head-inline">
        <div>
          <span>Contacts</span>
          <h5>Operational Contacts</h5>
          <p>Set the contact points used by internal teams and client-facing communication.</p>
        </div>
        <span class="hmo-contact-source" v-if="email && phone">Main contact available</span>
      </div>

      <div class="hmo-contact-grid">
        <div class="hmo-contact-card">
          <div class="hmo-contact-head">
            <h6>Management</h6>
            <button v-if="email && phone" type="button" @click="handleCopy('md')">Copy main</button>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="emailMd" type="email" v-model="emailMd" />
              <label class="active" for="emailMd">MD Email</label>
            </div>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="phoneMd" type="text" v-model="phoneMd" pattern="[0-9]{10,13}" />
              <label class="active" for="phoneMd">MD Phone</label>
            </div>
          </div>
        </div>

        <div class="hmo-contact-card">
          <div class="hmo-contact-head">
            <h6>Admin</h6>
            <button v-if="email && phone" type="button" @click="handleCopy('admin')">Copy main</button>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="emailSuperAdmin" type="email" v-model="emailSuperAdmin" />
              <label class="active" for="emailSuperAdmin">Admin Email</label>
            </div>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="phoneSuperAdmin" type="text" v-model="phoneSuperAdmin" pattern="[0-9]{10,13}" />
              <label class="active" for="phoneSuperAdmin">Admin Phone</label>
            </div>
          </div>
        </div>

        <div class="hmo-contact-card">
          <div class="hmo-contact-head">
            <h6>Customer Care</h6>
            <button v-if="email && phone" type="button" @click="handleCopy('care')">Copy main</button>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="emailCustomerCare" type="email" v-model="emailCustomerCare" />
              <label class="active" for="emailCustomerCare">Customer Care Email</label>
            </div>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="phoneCustomerCare" type="text" v-model="phoneCustomerCare" pattern="[0-9]{10,13}" />
              <label class="active" for="phoneCustomerCare">Customer Care Phone</label>
            </div>
          </div>
        </div>

        <div class="hmo-contact-card">
          <div class="hmo-contact-head">
            <h6>Finance</h6>
            <button v-if="email && phone" type="button" @click="handleCopy('finance')">Copy main</button>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="emailFinance" type="email" v-model="emailFinance" />
              <label class="active" for="emailFinance">Finance Email</label>
            </div>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="phoneFinance" type="text" v-model="phoneFinance" pattern="[0-9]{10,13}" />
              <label class="active" for="phoneFinance">Finance Phone</label>
            </div>
          </div>
        </div>

        <div class="hmo-contact-card">
          <div class="hmo-contact-head">
            <h6>Backup</h6>
            <button v-if="email && phone" type="button" @click="handleCopy('backup')">Copy main</button>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="emailBackup" type="email" v-model="emailBackup" />
              <label class="active" for="emailBackup">Backup Email</label>
            </div>
          </div>
          <div class="hmo-field-card">
            <div class="setup-field">
              <input id="phoneBackup" type="text" v-model="phoneBackup" pattern="[0-9]{10,13}" />
              <label class="active" for="phoneBackup">Backup Phone</label>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="hmo-form-section">
      <div class="hmo-section-head">
        <span>Digital Assets</span>
        <h5>Web Assets</h5>
        <p>Keep public web links and social references available for front-facing surfaces.</p>
      </div>

      <div class="hmo-field-grid">
        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="logoUrl" type="text" v-model="logoUrl" />
            <label class="active" for="logoUrl">Logo Link</label>
          </div>
        </div>

        <div class="hmo-field-card">
          <div class="setup-field">
            <input id="webAddress" type="text" v-model="webAddress" />
            <label class="active" for="webAddress">Website Link</label>
          </div>
        </div>

        <div class="hmo-field-card hmo-field-wide">
          <div class="setup-field">
            <textarea id="socialHandles" v-model="socialHandles"></textarea>
            <label class="active" for="socialHandles">Social Handles</label>
          </div>
        </div>
      </div>
    </section>

    <section class="hmo-form-section hmo-readonly-section">
      <div class="hmo-section-head">
        <span>Application Links</span>
        <h5>Application Links</h5>
        <p>These application endpoints are managed by the system and are shown for reference.</p>
      </div>

      <div class="hmo-app-grid">
        <div class="hmo-app-link">
          <small>Easy Server</small>
          <strong>{{ easyServer || "Not available" }}</strong>
        </div>
        <div class="hmo-app-link">
          <small>Easy Admin</small>
          <strong>{{ easyAdmin || "Not available" }}</strong>
        </div>
        <div class="hmo-app-link">
          <small>Easy Provider</small>
          <strong>{{ easyProvider || "Not available" }}</strong>
        </div>
        <div class="hmo-app-link">
          <small>Easy Client</small>
          <strong>{{ easyClient || "Not available" }}</strong>
        </div>
        <div class="hmo-app-link">
          <small>Easy User</small>
          <strong>{{ easyUser || "Not available" }}</strong>
        </div>
      </div>
    </section>

    <div class="hmo-form-actions">
      <button class="setup-primary-button" :disabled="isSubmitting">
        <span v-if="isSubmitting" class="setup-button-spinner" aria-hidden="true"></span>
        <CheckCircle2 v-else :size="17" />
        {{ isSubmitting ? "Updating..." : "Save HMO Profile" }}
      </button>
    </div>
  </form>
</template>
  
<script>
import { onMounted, ref } from "vue";
import { CheckCircle2 } from "@lucide/vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HmoComponent",
  components: { CheckCircle2, LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    // Workmen
    const response = ref(null);
    const error = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);

    // Owner's table
    const hmoName = ref(null);
    const hmoSlogan = ref(null);
    const email = ref(null);
    const emailMd = ref(null);
    const emailBackup = ref(null);
    const emailCustomerCare = ref(null);
    const emailFinance = ref(null);
    const emailSuperAdmin = ref(null);
    const phone = ref(null);
    const phoneMd = ref(null);
    const phoneBackup = ref(null);
    const phoneCustomerCare = ref(null);
    const phoneFinance = ref(null);
    const phoneSuperAdmin = ref(null);
    const addressHq = ref(null);
    const aboutHmo = ref(null);
    const generalNote = ref(null);
    const logoUrl = ref(null);
    const webAddress = ref(null);
    const socialHandles = ref(null);
    const easyServer = ref(null);
    const easyAdmin = ref(null);
    const easyProvider = ref(null);
    const easyClient = ref(null);
    const easyUser = ref(null);
    const wallet = ref(null);
    const spent = ref(null);
    const total = ref(null);
    const status = ref(null);

    // start here
    onMounted(() => {
      // get items
      (async () => {
        const { res, err } = await getItems("owner");

        if (res.value) {
          response.value = res.value;

          // assign
          hmoName.value = response.value.data.hmo_name;
          hmoSlogan.value = response.value.data.hmo_slogan;
          email.value = response.value.data.email;
          emailMd.value = response.value.data.email_md;
          emailBackup.value = response.value.data.email_backup;
          emailCustomerCare.value = response.value.data.email_customer_care;
          emailFinance.value = response.value.data.email_finance;
          emailSuperAdmin.value = response.value.data.email_super_admin;
          phone.value = response.value.data.phone;
          phoneMd.value = response.value.data.phone_md;
          phoneBackup.value = response.value.data.phone_backup;
          phoneCustomerCare.value = response.value.data.phone_customer_care;
          phoneFinance.value = response.value.data.phone_finance;
          phoneSuperAdmin.value = response.value.data.phone_super_admin;
          addressHq.value = response.value.data.address_hq;
          aboutHmo.value = response.value.data.about_hmo;
          generalNote.value = response.value.data.general_note;
          logoUrl.value = response.value.data.logo_url;
          webAddress.value = response.value.data.web_address;
          socialHandles.value = response.value.data.social_handles;
          easyServer.value = response.value.data.easy_server;
          easyAdmin.value = response.value.data.easy_admin;
          easyProvider.value = response.value.data.easy_provider;
          easyClient.value = response.value.data.easy_client;
          easyUser.value = response.value.data.easy_user;
          wallet.value = response.value.data.wallet;
          spent.value = response.value.data.spent;
          total.value = response.value.data.total;
          status.value = response.value.data.status;

          // is now Ready
          isReady.value = true;
        } else {
          error.value = err.value;
        }
      })();
    });

    // copy
    const handleCopy = (type) => {
      // start
      if (type == "md") {
        emailMd.value = email.value;
        phoneMd.value = phone.value;
      } else if (type == "admin") {
        emailSuperAdmin.value = email.value;
        phoneSuperAdmin.value = phone.value;
      } else if (type == "care") {
        emailCustomerCare.value = email.value;
        phoneCustomerCare.value = phone.value;
      } else if (type == "finance") {
        emailFinance.value = email.value;
        phoneFinance.value = phone.value;
      } else if (type == "backup") {
        emailBackup.value = email.value;
        phoneBackup.value = phone.value;
      }
    };

    // submit
    const handleSubmit = async () => {
      // start
      isSubmitting.value = true;
      error.value = false;

      // Native form validation handles custom errors \\

      // new status
      let newStatus = status.value;
      if (newStatus != "d") {
        newStatus = "a";
      }

      // move
      const val = {
        hmo_name: hmoName.value,
        hmo_slogan: hmoSlogan.value,
        email: email.value,
        email_md: emailMd.value,
        email_backup: emailBackup.value,
        email_customer_care: emailCustomerCare.value,
        email_finance: emailFinance.value,
        email_super_admin: emailSuperAdmin.value,
        phone: phone.value,
        phone_md: phoneMd.value,
        phone_backup: phoneBackup.value,
        phone_customer_care: phoneCustomerCare.value,
        phone_finance: phoneFinance.value,
        phone_super_admin: phoneSuperAdmin.value,
        address_hq: addressHq.value,
        about_hmo: aboutHmo.value,
        general_note: generalNote.value,
        logo_url: logoUrl.value,
        web_address: webAddress.value,
        social_handles: socialHandles.value,
        status: newStatus,
      };

      // post item
      const { res, err } = await putItem("owner", 1, val);

      if (res.value) {
        response.value = res.value;
        // next
        // emit new parameters
        context.emit("next", {
          step: 1,
          isComplete: true,
        });

      } else {
        error.value = err.value;
      }

      // end here
      isSubmitting.value = false;
    };

    return {
      error,
      isReady,
      isSubmitting,
      handleCopy,
      handleSubmit,
      hmoName,
      hmoSlogan,
      email,
      emailMd,
      emailBackup,
      emailCustomerCare,
      emailFinance,
      emailSuperAdmin,
      phone,
      phoneMd,
      phoneBackup,
      phoneCustomerCare,
      phoneFinance,
      phoneSuperAdmin,
      addressHq,
      aboutHmo,
      generalNote,
      logoUrl,
      webAddress,
      socialHandles,
      easyServer,
      easyAdmin,
      easyProvider,
      easyClient,
      easyUser,
    };
  },
};
</script>
  
<style scoped>
.hmo-settings-form {
  display: grid;
  gap: 18px;
}

.hmo-form-section {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  overflow: hidden;
}

.hmo-section-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 22px 24px;
}

.hmo-section-head-inline {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.hmo-section-head span,
.hmo-contact-source {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
}

.hmo-section-head h5 {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 4px 0 0;
}

.hmo-section-head p {
  color: #607d8b;
  font-size: 0.96rem;
  line-height: 1.55;
  margin: 9px 0 0;
}

.hmo-contact-source {
  background: #eef7f6;
  border: 1px solid #c9e5e1;
  border-radius: 999px;
  color: #00695c;
  flex: 0 0 auto;
  padding: 8px 12px;
}

.hmo-field-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px;
}

.hmo-field-wide {
  grid-column: 1 / -1;
}

.hmo-field-card {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  padding: 8px 16px 2px;
}

.setup-field { display: flex; flex-direction: column-reverse; gap: .4rem; }
.hmo-field-card input,
.hmo-field-card textarea {
  background: var(--ui-background);
  border: 1px solid var(--ui-input);
  border-radius: calc(var(--ui-radius) - 2px);
  color: #263238;
  font: inherit;
  font-weight: 600;
  margin: 0;
  min-height: 2.6rem;
  padding: .6rem .75rem;
  width: 100%;
}
.hmo-field-card textarea { min-height: 6rem; resize: vertical; }
.hmo-field-card label {
  color: #607d8b;
  font-size: .75rem;
  font-weight: 700;
  position: static;
  transform: none;
}

.hmo-contact-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px;
}

.hmo-contact-card {
  background: #fbfcfc;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  padding: 14px;
}

.hmo-contact-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.hmo-contact-head h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.hmo-contact-head button {
  background: #ffffff;
  border: 1px solid #d6e1e4;
  border-radius: 999px;
  color: #00695c;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 7px 11px;
}

.hmo-contact-head button:hover {
  background: #eef7f6;
}

.hmo-readonly-section {
  background: #fbfcfc;
}

.hmo-app-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px;
}

.hmo-app-link {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  padding: 16px;
}

.hmo-app-link small,
.hmo-app-link strong {
  display: block;
}

.hmo-app-link small {
  color: #607d8b;
  font-size: 0.8rem;
  font-weight: 800;
  margin-bottom: 7px;
}

.hmo-app-link strong {
  color: #263238;
  font-size: 0.92rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.hmo-form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 18px;
}

.setup-primary-button {
  align-items: center;
  background: #00897b;
  border: 1px solid #00897b;
  border-radius: 6px;
  box-shadow: 0 12px 24px -18px rgba(0, 77, 64, 0.65);
  color: #fff;
  display: inline-flex;
  gap: .5rem;
  font-weight: 800;
  letter-spacing: 0;
  min-height: 2.6rem;
  padding: 0 1rem;
  text-transform: none;
}
.setup-primary-button:disabled { cursor: wait; opacity: .6; }
.setup-button-spinner { animation: setup-spin .7s linear infinite; border: 2px solid rgb(255 255 255 / .35); border-radius: 50%; border-top-color: #fff; height: 1rem; width: 1rem; }
@keyframes setup-spin { to { transform: rotate(360deg); } }

@media only screen and (max-width: 992px) {
  .hmo-field-grid,
  .hmo-contact-grid,
  .hmo-app-grid {
    grid-template-columns: 1fr;
  }

  .hmo-section-head-inline {
    flex-direction: column;
  }
}
</style>
